import React, { useMemo, useState } from 'react';
import { useFnReducer, StateFn, SetterStore, useSetter } from '../hooks/utils';
import { BitMap, Color } from '@/src/components/canvas/bitmap';
import { produce } from 'immer';
import { DrawMode, DrawTool } from '@/src/components/canvas/types';
import { EntryTableState, EntryTableStore, useEntryTable } from '../hooks/useEntryTable';
import { useKeyPressEvent } from 'react-use';

export interface BlockGroup {
  /**
   * 色块组名称
   */
  name: string;
  /**
   * 色块组宽度
   */
  groupWidth: number;
  /**
   * 色块组高度
   */
  groupHeight: number;
  /**
   * 色块尺寸
   */
  blockSize: number;
}

export interface ColorBitMap {
  /**
   * 所属的组ID
   */
  groupID: number;
  /**
   * 色块
   */
  map: BitMap<Color>;
}
export interface Options {
  /**
   * 色块像素尺寸
   */
  pixelSize: number;
  /**
   * 当前画笔颜色
   */
  color: Color;
  /**
   * 是否开启网格
   */
  grid: boolean;
  /**
   * 绘画模式
   */
  drawMode: DrawMode;
  /**
   * 绘画工具
   */
  tool: DrawTool;
  /**
   * 当前鼠标在画布上的位置
   */
  pos: [number, number];
}
export interface EditorContextStore {

  /**
   * 当前正在编辑的色块ID
   */
  activeBitMapID: number;
  setActiveBitMapID: (id: number) => void,

  /**
   * 单值选项
   */
  options: SetterStore<Options>;
  /**
   * 色块组表
   */
  blockGroups: EntryTableStore<BlockGroup>;
  /**
   * 色块表
   */
  colorBitMaps: EntryTableStore<ColorBitMap>;

  /**
   * 添加色块组并添加相引数量的色块到色块表
   */
  appendBlockGroup: (name: string, groupWidth: number, groupHeight: number, blockSize: number) => void;

}



const EditorContext = React.createContext<EditorContextStore>(undefined);

export const useEditorContext = () => React.useContext(EditorContext);

export function useEditorContextStore(): EditorContextStore {
  const options = useSetter<Options>({
    color: Color.black,
    grid: true,
    pixelSize: 20,
    pos: [0, 0],
    tool: DrawTool.pancel,
    drawMode: DrawMode.normal,
  });
  const [activeBitMapID, setActiveBitMapID] = useState<number>();
  const blockGroups = useEntryTable<BlockGroup>();
  const colorBitMaps = useEntryTable<ColorBitMap>();

  const appendBlockGroup = (name: string, groupWidth: number, groupHeight: number, blockSize: number) => {
    const groupID = blockGroups.table.autoID;
    blockGroups.insert({
      blockSize,
      groupHeight,
      groupWidth,
      name,
    });
    colorBitMaps.insert(...(new Array(groupWidth * groupHeight)
      .fill(0)
      .map(() => ({
        groupID,
        map: new BitMap<Color>(blockSize, blockSize, Color.none)
      }))));
  }

  useKeyPressEvent(e => e.key === 'q', () => {
    if (options.get.tool === DrawTool.pancel) {
      options.set.tool(DrawTool.bucket)
    } else if (options.get.tool === DrawTool.bucket) {
      options.set.tool(DrawTool.pancel)
    }
  });
  useKeyPressEvent(e => e.key === 'w', () => {
    if (options.get.drawMode === DrawMode.normal) {
      options.set.drawMode(DrawMode.lrs);
    } else if (options.get.drawMode === DrawMode.lrs) {
      options.set.drawMode(DrawMode.uds);
    } else if (options.get.drawMode === DrawMode.uds) {
      options.set.drawMode(DrawMode.centrosymmetry);
    } else if (options.get.drawMode === DrawMode.centrosymmetry) {
      options.set.drawMode(DrawMode.normal);
    }
  });
  return {
    activeBitMapID,
    setActiveBitMapID,
    options,
    blockGroups,
    colorBitMaps,
    appendBlockGroup,
  }
}



export function EditorContextProvder(props: { children?: React.ReactElement | React.ReactElement[] }) {

  return (
    <EditorContext.Provider
      value={useEditorContextStore()}
    >
      {props.children}
    </EditorContext.Provider>
  );
};